'use client';

import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { mustActivateProductSection } from '../../utils/deferred-product-intent';

const subscribeHydration = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;
const activationEvents = ['pointerdown', 'touchstart', 'focusin', 'keydown', 'click', 'auxclick'] as const;

function createGate() {
  let resolve!: () => void;
  const promise = new Promise<void>((done) => { resolve = done; });
  return {
    released: false,
    promise,
    release() {
      if (this.released) return;
      // React's synchronous event hydration must see this BEFORE the promise retry.
      this.released = true;
      resolve();
    },
  };
}

function HydrationGate({ gate, children, hostId }: { gate: ReturnType<typeof createGate>; children: ReactNode; hostId: string }) {
  // Inside the suspended child: its post-hydration snapshot check cannot update
  // the already committed parent while this server boundary is still deferred.
  const isClientMount = useSyncExternalStore(subscribeHydration, clientSnapshot, serverSnapshot);
  useEffect(() => {
    // DOM-only diagnostic: verify that this opt-in island actually hydrated.
    document.getElementById(hostId)?.setAttribute('data-product-enhanced', 'true');
  }, [hostId]);
  if (typeof document !== 'undefined') {
    if (mustActivateProductSection(hostId)) gate.release();
  }
  if (typeof window !== 'undefined' && !isClientMount && !gate.released) throw gate.promise;
  return children;
}

/** Modra-only pilot: SSR content stays visible; only initial hydration may wait. */
export default function DeferredProductSection({
  children,
  id,
  className,
  style,
  rootMargin = 200,
}: {
  children: ReactNode;
  id: string;
  className?: string;
  style?: CSSProperties;
  rootMargin?: number;
}) {
  const element = useRef<HTMLDivElement>(null);
  const [gate] = useState(createGate);
  const boundary = useMemo(() => (
    <Suspense fallback={null}>
      <HydrationGate gate={gate} hostId={id}>{children}</HydrationGate>
    </Suspense>
  ), [children, gate, id]);

  useLayoutEffect(() => {
    const node = element.current;
    if (!node || gate.released) return;
    let observer: IntersectionObserver | undefined;
    const activate = () => { gate.release(); observer?.disconnect(); };
    // Server cards are public. Never intentionally delay account-specific prices
    // or let a first authenticated action use an old guest snapshot.
    const unsubscribeCustomer = useCustomerStore.subscribe((next, previous) => {
      if (next.me?.customer.id !== previous.me?.customer.id || next.me?.authToken !== previous.me?.authToken) activate();
    });
    if (useCustomerStore.getState().me !== null) activate();
    const onInteraction = (event: Event) => {
      if (event.target instanceof Node && node.contains(event.target)) activate();
    };
    for (const event of activationEvents) window.addEventListener(event, onInteraction, { capture: true, passive: true });
    if (node.contains(document.activeElement)) activate();
    const bounds = node.getBoundingClientRect();
    // A click/scroll may hydrate the outer boundary before IO has delivered its
    // first callback. Visible cards must be interactive in that same commit.
    if (bounds.top <= window.innerHeight + rootMargin && bounds.bottom >= -rootMargin) activate();
    if (!gate.released) {
      try {
        if (!('IntersectionObserver' in window)) activate();
        else {
          observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) activate();
          }, { rootMargin: `${rootMargin}px` });
          observer.observe(node);
        }
      } catch { activate(); }
    }
    return () => {
      observer?.disconnect();
      unsubscribeCustomer();
      for (const event of activationEvents) window.removeEventListener(event, onInteraction, true);
      // Never leave a pending React retry attached to an unmounted instance.
      gate.release();
    };
  }, [gate, rootMargin]);

  return <div ref={element} id={id} className={className} style={style} data-deferred-product-section="viewport">{boundary}</div>;
}
